import { SelectionModel } from '@angular/cdk/collections';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { BehaviorSubject, Subject, of } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { WorkerAgreementService } from 'src/app/data/worker-agreement.service';
import { Filter } from 'src/app/models/common/filter';
import { AllWorkerAgreementGridDto } from 'src/app/models/dtos/AllWorkerAgreementGridDto';
import { IPagedResult } from 'src/app/shared/models/PagedResult';
import { GridTableDataSource } from 'src/app/virtual-scroll/data-source';

export class AllWorkerAgreementsGridDataSource extends GridTableDataSource<AllWorkerAgreementGridDto> {
  isLoading$ = new BehaviorSubject<boolean>(false);
  selection = new SelectionModel<AllWorkerAgreementGridDto>(true, []);
  private cancelationSubject = new Subject();
  private countSubject = new BehaviorSubject<number>(0);
  private dataLoadedSubject = new Subject<void>();
  private _absoluteDataLength: number;

  count$ = this.countSubject.asObservable();
  dataLoaded$ = this.dataLoadedSubject.asObservable();

  get isLoading() {
    return this.isLoading$.getValue();
  }

  get absoluteDataLength() {
    return this._absoluteDataLength;
  }

  public get isAllSelected() {
    return this.allData?.length && this.selection.selected?.length === this.allData.length;
  }

  public isSelected(item: AllWorkerAgreementGridDto): boolean {
    return this.selection.isSelected(item);
  }

  public selectAll(): void {
    this.selection.select(...this.allData);
  }

  public deselectAll(): void {
    this.selection.clear();
  }

  constructor(
    private workerAgreementService: WorkerAgreementService,
    { viewport }: { viewport?: CdkVirtualScrollViewport } = {}
  ) {
    super([], { viewport });
  }

  disconnect() {
    this.cancelationSubject.next(undefined);
    this.isLoading$.next(false);
  }

  reset() {
    this.cancelationSubject.next(undefined);
    this.isLoading$.next(false);
    this.deselectAll();
    this.allData = [];
  }

  fetchWorkerAgreements(page: number, count: number, sortingField: string, sortingDirection: string, filters: Filter[]) {
    this.isLoading$.next(true);
    this.workerAgreementService
      .getAllWorkersAgreements(page, count, sortingField, sortingDirection, filters)
      .pipe(
        takeUntil(this.cancelationSubject),
        catchError(() => of([])),
        finalize(() => this.isLoading$.next(false)),
      )
      .subscribe((response: IPagedResult<AllWorkerAgreementGridDto>) => {
        this._absoluteDataLength = response.Count;
        const isAllSelected = this.isAllSelected;
        this.merge(response.Results, page, count);
        this.keepSelectionState(isAllSelected);
        this.countSubject.next(response.Count);
        this.dataLoadedSubject.next();
      });
  }

  private keepSelectionState(isAllSelected: boolean) {
    let old = [...this.selection.selected];
    this.selection.clear();
    this.allData?.forEach((a) => (isAllSelected || old.some((r) => r.AgreementId === a.AgreementId)) && this.selection.select(a));
  };

  private merge(data: AllWorkerAgreementGridDto[], page: number, count: number) {
    const tmp = this.allData.filter((item, index) => {
      return (index < count * (page - 1) || index >= count * page) || data.some(incoming => incoming.AgreementId === item.AgreementId)
    });

    data?.map(item => {
      const index = tmp.findIndex(td => td.AgreementId === item.AgreementId)
      if (index > -1) {
        tmp[index] = item;
      } else {
        const index = count * (page - 1) + data.indexOf(item);

        if (index > -1) {
          tmp.splice(index, 0, item);
        } else {
          tmp.push(item);
        }
      }
    },
      (err: any) => console.log(err));

    this.allData = tmp;
  }
}
